<template>
  <div>
    <Filters
      :filters="quarantineFilters"
      filter-key="quarantine"
      :initial-data="filtersStatus.quarantine"
      :loading="filtersLoading"
    />

    <TableWrapper
      :table-columns="quarantineColumns"
      table-key="quarantine"
      @edit="goToEdit"
      @accept="acceptSample"
      @refuse="refuseSample"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <CreateEditSample
      :visible="openCreateSampleModal"
      :is-modal-edit="isModalEdit"
      :initial-data="initialSampleData"
      @dialogClose="handleSampleDialogClose"
    />

    <AcceptRefuseSample
      v-if="openAcceptRefuseModal"
      :isAccept="acceptRefuseModalAccept"
      :sample="selectedSample"
      @dialogClose="handleAcceptRefuseDialogClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import Filters from '@/components/Filters.vue';
  import { quarantineColumns, quarantineFilters } from '@/config';
  import { quarantineApi } from '@/api';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  import CreateEditSample from './CreateEditSample.vue';
  import AcceptRefuseSample from './AcceptRefuseSample.vue';

  export default {
    name: 'QuarantineIndex',
    components: {
      TableWrapper,
      Filters,
      CreateEditSample,
      ViewActionButtons,
      AcceptRefuseSample,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        quarantineColumns,
        quarantineFilters,
        openCreateSampleModal: false,
        isModalEdit: false,
        initialSampleData: {},
        openAcceptRefuseModal: false,
        acceptRefuseModalAccept: false,
        selectedSample: null,
        actionButtons: [
          {
            text: this.$t('quarantine.create.createSample'),
            type: 'primary',
            permissions: ['quarantineManage'],
            action: () => {
              this.openCreateSampleModal = true;
            },
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const quarantineIndexResponse = await quarantineApi.index(
            this.tableFilters('quarantine'),
            this.filtersStatus.quarantine,
            { signal }
          );
          const quarantineCountResponse = await quarantineApi.count(
            this.filtersStatus.quarantine,
            { signal }
          );
          this.updateTableRows({
            tableKey: 'quarantine',
            rows: quarantineIndexResponse.data,
          });
          this.updateTableCount({
            tableKey: 'quarantine',
            count: quarantineCountResponse.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async getSample() {
        try {
          const sample = await quarantineApi.show(this.$route.params.sampleId);
          this.initialSampleData = { ...sample.data };
          this.isModalEdit = true;
          this.openCreateSampleModal = true;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
          this.$router.push({ name: 'quarantineIndex' });
        }
      },
      handleSampleDialogClose(status) {
        this.openCreateSampleModal = false;
        this.initialSampleData = {};
        if (status.success) this.tableRows();
        if (this.isModalEdit) {
          this.isModalEdit = false;
          this.$router.push({ name: 'quarantineIndex' });
        }
      },
      goToEdit(row) {
        this.$router.push({
          name: 'quarantineEdit',
          params: { sampleId: row.id },
        });
      },
      acceptSample(row) {
        this.selectedSample = row;
        this.acceptRefuseModalAccept = true;
        this.openAcceptRefuseModal = true;
      },
      refuseSample(row) {
        this.selectedSample = row;
        this.acceptRefuseModalAccept = false;
        this.openAcceptRefuseModal = true;
      },
      handleAcceptRefuseDialogClose(status) {
        this.openAcceptRefuseModal = false;
        this.acceptRefuseModalAccept = false;
        this.selectedSample = null;
        if (status.success) this.tableRows();
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.tableRows();
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        if (to.params.sampleId) vm.getSample();
      });
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>
